// ===============================================
// Create Purchase Order Page (Updated)
// หน้าสร้างใบสั่งซื้อใหม่ - ใช้งานกับระบบ contacts
// ===============================================

import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Row,
  Col,
  Select,
  DatePicker,
  Radio,
  Button,
  Table,
  InputNumber,
  Input,
  Space,
  Typography,
  Divider,
  message,
  Modal,
  AutoComplete,
  Alert,
} from 'antd';
import {
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  purchasesAPI,
  suppliersAPI,
  productsAPI,
  formatCurrency,
  handleAPIError,
  PO_STATUS,
  PAYMENT_TYPES
} from '../../services/PurchaseService';
import productService from '../../services/productService'
import ContactsService from '../../services/contactService';
import { useAuth } from '../../contexts/AuthContext'
import { useUnitsOptions } from '../../hooks/useUnits';

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;


const CreatePurchaseOrderPage = () => {
  const { user, hasPermission } = useAuth()
  const navigate = useNavigate();
  const { id } = useParams(); // ดึงค่าพารามิเตอร์จาก URL
  //console.log("ID จาก URL:", id); // ← ดูใน console
  const isEdit = !!id;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [paymentType, setPaymentType] = useState(PAYMENT_TYPES.CASH);
  const [items, setItems] = useState([]);
  const { unitsOptions, loading: unitsLoading } = useUnitsOptions({
    includeCode: false,
    valueField: 'unit_name',
    sortBy: 'display_order'
  });

  const [totals, setTotals] = useState({
    subtotal: 0,
    discount: 0,
    tax: 0,
    base_amount: 0,     // ยอดก่อนภาษี (สำหรับโหมด Include)
    total: 0,
  });

  // โหมดการคำนวณภาษี
  const TAX_MODES = {
    INCLUDE: 'include',    // ราคารวมภาษีแล้ว
    EXCLUDE: 'exclude',    // ราคาไม่รวมภาษี
    NO_VAT: 'novat'        // ไม่มีภาษี
  };

  // Modal สำหรับเพิ่มสินค้า
  const [addItemModal, setAddItemModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [productOptions, setProductOptions] = useState([]);


  const [taxMode, setTaxMode] = useState(TAX_MODES.EXCLUDE); //วิธีคำนวณภาษี

  // จัดการการเปลี่ยนโหมดภาษี
  const handleTaxModeChange = (mode) => {
    setTaxMode(mode);

    if (mode === TAX_MODES.NO_VAT) {
      form.setFieldValue('tax_rate', 0);
    } else if (mode === TAX_MODES.EXCLUDE && !form.getFieldValue('tax_rate')) {
      form.setFieldValue('tax_rate', 7);
    } else if (mode === TAX_MODES.INCLUDE && !form.getFieldValue('tax_rate')) {
      form.setFieldValue('tax_rate', 7);
    }
  };

  // ดึงข้อมูลผู้จำหน่าย (จาก contacts)
  const fetchSuppliers = async () => {
    try {
      const response = await ContactsService.getSuppliersForDropdown();
      console.log('response', response.data);
      if (response.success) {
        setSuppliers(response.data);
      }
    } catch (error) {
      message.error(handleAPIError(error));
    }
  };

  // ดึงข้อมูลสินค้า
  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getForPurchase();
      if (response.success) {
        setProducts(response.data);
      }
    } catch (error) {
      message.error(handleAPIError(error));
    }
  };

  // ดึงข้อมูลใบสั่งซื้อสำหรับแก้ไข
  const fetchPurchaseOrder = async () => {

    console.log('id:', id);

    if (!isEdit) return;

    setLoading(true);
    try {
      const response = await purchasesAPI.getById(id);
      if (response.success) {
        const data = response.data;

        console.log('response.data', response.data);

        // ตั้งค่าข้อมูลในฟอร์ม
        form.setFieldsValue({
          ...data,
          order_date: dayjs(data.order_date),
          due_date: data.due_date ? dayjs(data.due_date) : null,
        });

        // ตั้งค่าโหมดภาษี (ถ้ามีข้อมูล)
        if (data.vat_mode) {
          setTaxMode(data.vat_mode);
        } else if (data.tax_rate === 0) {
          setTaxMode(TAX_MODES.NO_VAT);
        } else {
          setTaxMode(TAX_MODES.INCLUDE);
        }

        setSelectedSupplier(data.supplier);
        setPaymentType(data.payment_type);
        setItems(data.items || []);
      }
    } catch (error) {
      message.error(handleAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  // ค้นหาสินค้า
  const searchProducts = async (value) => {
    if (value.length >= 2) {
      try {
        const response = await productsAPI.search(value);
        console.log('searchProducts', response.data);
        if (response.success) {
          const options = response.data.map(product => ({
            value: product.id,
            label: `${product.barcode} - ${product.name} (คงเหลือ: ${product.stock || 0})`,
            product,
          }));
          setProductOptions(options);
        }
      } catch (error) {
        console.error('Error searching products:', error);
      }
    } else {
      setProductOptions([]);
    }
  };

  // เลือกผู้จำหน่าย
  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setSelectedSupplier(supplier);

    // ตั้งค่าเริ่มต้นตามเงื่อนไขการชำระเงินของผู้จำหน่าย
    if (supplier) {
      if (supplier.payment_terms > 0) {
        setPaymentType(PAYMENT_TYPES.CREDIT);
        form.setFieldValue('payment_type', PAYMENT_TYPES.CREDIT);

        // คำนวณวันครบกำหนด
        const dueDate = dayjs().add(supplier.payment_terms, 'day');
        form.setFieldValue('due_date', dueDate);
      } else {
        setPaymentType(PAYMENT_TYPES.CASH);
        form.setFieldValue('payment_type', PAYMENT_TYPES.CASH);
        form.setFieldValue('due_date', null);
      }
    }
  };

  // เลือกสินค้าสำหรับเพิ่มในรายการ
  const handleProductSelect = (value, option) => {
    setSelectedProduct(option.product);
  };

  // เพิ่มสินค้าลงในรายการ
  const handleAddItem = () => {
    if (!selectedProduct) {
      message.warning('กรุณาเลือกสินค้า');
      return;
    }

    const existingItemIndex = items.findIndex(item => item.product_id === selectedProduct.id);

    console.log('selected Product:', selectedProduct);

    if (existingItemIndex >= 0) {
      // หากมีสินค้านี้แล้ว ให้เพิ่มจำนวน
      const newItems = [...items];
      newItems[existingItemIndex].quantity += 1;
      setItems(newItems);
    } else {
      // เพิ่มสินค้าใหม่
      const newItem = {
        id: Date.now(), // temporary ID
        product_id: selectedProduct.id,
        product_sku: selectedProduct.barcode,
        barcode: selectedProduct.barcode,
        product_name: selectedProduct.name,
        cost: selectedProduct.cost,
        price: selectedProduct.price,
        notes: selectedProduct.description,
        unit_cost: selectedProduct.cost || 0,
        unit: selectedProduct.unit || 'ชิ้น',
        category: selectedProduct.category,
        brand: selectedProduct.brand,
        quantity: 1,
        total_cost: selectedProduct.cost || 0,
      };
      setItems([...items, newItem]);
    }

    // รีเซ็ตการเลือกสินค้า
    setSelectedProduct(null);
    setProductSearch('');
    setAddItemModal(false);
  };

  // อัพเดตรายการสินค้า
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    // คำนวณยอดรวมของรายการ
    if (field === 'quantity' || field === 'unit_cost') {
      newItems[index].total_cost = newItems[index].quantity * newItems[index].unit_cost;
    }

    setItems(newItems);
  };

  // ลบรายการสินค้า
  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  // คำนวณยอดรวม
  /**
 * calculateTotals robust version
 * params:
 *  - items: array of items (แต่ละตัวมี total_cost หรือ quantity & unit_cost)
 *  - form: form instance ที่มี fields 'discount_amount' (หรือ 'discount_percent'), 'discount_mode' ('amount'|'percent'), 'tax_rate'
 *  - taxMode: TAX_MODES.INCLUDE | TAX_MODES.EXCLUDE | TAX_MODES.NO_VAT
 */

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + Number(item.total_cost || 0), 0);
    const discount = Number(form.getFieldValue('discount_amount') || 0);
    const taxRate = Number(form.getFieldValue('tax_rate') || 0);

    let tax = 0;
    let total = 0;
    let base_amount = subtotal;

    switch (taxMode) {
      case TAX_MODES.INCLUDE: {
        const subtotalWithTax = subtotal - discount;
        tax = (subtotalWithTax * taxRate) / (100 + taxRate);
        base_amount = subtotalWithTax - tax;
        total = subtotalWithTax;
        break;
      }

      // case TAX_MODES.EXCLUDE: {
      //   // ✅ ถ้าราคาสินค้าใน subtotal รวม VAT อยู่ → ต้องแยกออกก่อน
      //   let raw_base = subtotal - discount;
      //   const cleaned_base = (raw_base * 100) / (100 + taxRate); // แยกภาษีออก

      //   base_amount = cleaned_base;
      //   tax = cleaned_base * (taxRate / 100);
      //   total = base_amount + tax;
      //   break;
      // }

      case TAX_MODES.EXCLUDE: {
        base_amount = subtotal - discount;
        tax = base_amount * (taxRate / 100);
        total = base_amount + tax;
        break;
      }

      case TAX_MODES.NO_VAT:
      default: {
        base_amount = subtotal - discount;
        tax = 0;
        total = base_amount;
        break;
      }
    }

    setTotals({
      subtotal,
      discount,
      tax,
      total,
      base_amount,
    });
  };


  // เปลี่ยนประเภทการชำระเงิน
  const handlePaymentTypeChange = (type) => {
    setPaymentType(type);

    if (type === PAYMENT_TYPES.CASH) {
      form.setFieldValue('due_date', null);
    } else if (type === PAYMENT_TYPES.CREDIT && selectedSupplier?.payment_terms) {
      const dueDate = dayjs().add(selectedSupplier.payment_terms, 'day');
      form.setFieldValue('due_date', dueDate);
    }
  };

  // บันทึกใบสั่งซื้อ
  const handleSave = async (status = PO_STATUS.DRAFT) => {
    try {
      const values = await form.validateFields();

      console.log('user:', user);

      if (items.length === 0) {
        message.warning('กรุณาเพิ่มรายการสินค้าอย่างน้อย 1 รายการ');
        return;
      }

      setSaveLoading(true);

      const purchaseData = {
        ...values,
        status,
        items,
        subtotal: totals.subtotal,
        discount_amount: totals.discount,
        tax_amount: totals.tax,
        total_amount: totals.total,
        order_date: values.order_date.format('YYYY-MM-DD'),
        purchase_date: values.order_date.format('YYYY-MM-DD'),
        expected_date: values.expected_date?.format('YYYY-MM-DD'),
        due_date: values.due_date?.format('YYYY-MM-DD'),
        received_by: user.id
      };

      let response;
      if (isEdit) {
        response = await purchasesAPI.update(id, purchaseData);
      } else {
        response = await purchasesAPI.create(purchaseData);
      }

      if (response.success) {
        message.success(`${isEdit ? 'อัพเดต' : 'สร้าง'}ใบสั่งซื้อสำเร็จ`);
        navigate('/purchases');
      }
    } catch (error) {
      message.error(handleAPIError(error));
    } finally {
      setSaveLoading(false);
    }
  };

  // คอลัมน์ของตารางรายการสินค้า
  const itemColumns = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'barcode',
      key: 'barcode',
      width: 120,
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'product_name',
      key: 'product_name',
      ellipsis: true,
    },
    {
      title: 'ราคาต่อหน่วย',
      dataIndex: 'unit_cost',
      key: 'unit_cost',
      width: 150,
      render: (value, record, index) => (
        <InputNumber
          style={{ width: '100%' }}
          value={value}
          min={0}
          step={0.01}
          formatter={(value) => value ? formatCurrency(value).replace('฿', '') : ''}
          parser={(value) => value.replace(/,/g, '')}
          onChange={(value) => handleItemChange(index, 'unit_cost', value || 0)}
        />
      ),
    },
    {
      title: 'จำนวน',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (value, record, index) => (
        <InputNumber
          style={{ width: '100%' }}
          value={value}
          min={1}
          onChange={(value) => handleItemChange(index, 'quantity', value || 1)}
        />
      ),
    },
    {
      title: 'หน่วยนับ',
      dataIndex: 'unit',
      key: 'unit',
      width: 120,
      render: (value, record, index) => (
        // เพิ่ม loading และ notFoundContent
        <Select
          style={{ width: '100%' }}
          value={value}
          onChange={(value) => handleItemChange(index, 'unit', value)}
          placeholder="เลือกหน่วย"
          loading={unitsLoading}
          showSearch
          notFoundContent={unitsLoading ? "กำลังโหลด..." : "ไม่พบข้อมูล"}
        >
          {unitsOptions.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'ยอดรวม',
      dataIndex: 'total_cost',
      key: 'total_cost',
      width: 150,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'การดำเนินการ',
      key: 'actions',
      width: 80,
      render: (_, record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(index)}
        />
      ),
    },
  ];

  // เรียกใช้เมื่อ component mount
  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
    fetchPurchaseOrder();
  }, [id]);

  const discountAmount = Form.useWatch('discount_amount', form);
  const taxRate = Form.useWatch('tax_rate', form);

  // คำนวณยอดรวมเมื่อรายการเปลี่ยน
  useEffect(() => {
    calculateTotals();
  }, [items, taxMode, discountAmount, taxRate]);

  return (
    <div className="create-purchase-order">
      {/* Header */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/purchases')}
              >
                กลับ
              </Button>
              <Title level={4} style={{ margin: 0 }}>
                <ShoppingCartOutlined /> {isEdit ? 'แก้ไขใบสั่งซื้อ' : 'สร้างใบสั่งซื้อใหม่'}
              </Title>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                loading={saveLoading}
                onClick={() => handleSave(PO_STATUS.DRAFT)}
              >
                บันทึกร่าง
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={saveLoading}
                onClick={() => handleSave(PO_STATUS.PENDING)}
              >
                ส่งอนุมัติ
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          order_date: dayjs(),
          payment_type: PAYMENT_TYPES.CASH,
          tax_rate: 7,
        }}
      >
        {/* ข้อมูลทั่วไป */}
        <Card title="ข้อมูลใบสั่งซื้อ" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                label="วันที่สั่งซื้อ"
                name="order_date"
                rules={[{ required: true, message: 'กรุณาเลือกวันที่สั่งซื้อ' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                label="วันที่คาดว่าจะได้รับ"
                name="due_date"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                label="ผู้จำหน่าย"
                name="supplier_id"
                rules={[{ required: true, message: 'กรุณาเลือกผู้จำหน่าย' }]}
              >
                <Select
                  placeholder="เลือกผู้จำหน่าย"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleSupplierChange}
                >
                  {suppliers.map(supplier => (
                    <Option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                label="การชำระเงิน"
                name="payment_type"
                rules={[{ required: true, message: 'กรุณาเลือกการชำระเงิน' }]}
              >
                <Radio.Group
                  value={paymentType}
                  onChange={(e) => handlePaymentTypeChange(e.target.value)}
                >
                  <Radio value={PAYMENT_TYPES.CASH}>ชำระแล้ว</Radio>
                  <Radio value={PAYMENT_TYPES.CREDIT}>ยังไม่ชำระ</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            {paymentType === PAYMENT_TYPES.CREDIT && (
              <Col xs={24} sm={12} lg={6}>
                <Form.Item
                  label="วันครบกำหนดชำระเงิน"
                  name="due_date"
                  rules={[{ required: true, message: 'กรุณาเลือกวันครบกำหนดชำระเงิน' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            )}
            <Col xs={24}>
              <Form.Item label="หมายเหตุ" name="notes">
                <TextArea rows={2} placeholder="หมายเหตุเพิ่มเติม" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* รายการสินค้า */}
        <Card
          title="รายการสินค้า"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddItemModal(true)}
            >
              เพิ่มสินค้า
            </Button>
          }
          style={{ marginBottom: 24 }}
        >
          <Table
            columns={itemColumns}
            dataSource={items}
            rowKey={(record, index) => record.id || index}
            pagination={false}
            locale={{
              emptyText: 'ยังไม่มีรายการสินค้า กรุณาเพิ่มสินค้า',
            }}
          />
        </Card>

        {/* สรุปยอดเงิน */}
        <Card title="สรุปยอดเงิน">
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Row gutter={[16, 16]}>
                <Col xs={12}>
                  <Form.Item label="ส่วนลด (฿)" name="discount_amount">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      formatter={(value) => value ? formatCurrency(value).replace('฿', '') : ''}
                      parser={(value) => value.replace(/,/g, '')}
                      onChange={calculateTotals}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item label="ภาษี (%)" name="tax_rate">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      max={100}
                      step={0.01}
                      formatter={(value) => `${value}%`}
                      parser={(value) => value.replace('%', '')}
                      onChange={calculateTotals}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item label="วิธีคำนวณภาษี" name="vat_mode">
                    <Radio.Group value={taxMode} onChange={(e) => handleTaxModeChange(e.target.value)}>
                      <Radio.Button value={TAX_MODES.EXCLUDE}>ไม่รวมภาษี</Radio.Button>
                      <Radio.Button value={TAX_MODES.INCLUDE}>รวมภาษี</Radio.Button>
                      <Radio.Button value={TAX_MODES.NO_VAT}>ไม่มีภาษี</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col xs={24} lg={12}>
              <div style={{ textAlign: 'right', fontSize: '16px' }}>
                <div style={{ marginBottom: 8 }}>
                  <Text>ยอดรวมก่อนหักส่วนลด: </Text>
                  <Text strong>{formatCurrency(totals.subtotal)}</Text>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Text>ส่วนลด: </Text>
                  <Text>{formatCurrency(totals.discount)}</Text>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Text>ภาษี: </Text>
                  <Text>{formatCurrency(totals.tax)}</Text>
                </div>
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ fontSize: '18px' }}>
                  <Text strong>ยอดรวมสุทธิ: </Text>
                  <Text strong style={{ color: '#1890ff' }}>
                    {formatCurrency(totals.total)}
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      </Form>

      {/* Modal เพิ่มสินค้า */}
      <Modal
        title="เพิ่มสินค้า"
        open={addItemModal}
        onOk={handleAddItem}
        onCancel={() => {
          setAddItemModal(false);
          setSelectedProduct(null);
          setProductSearch('');
        }}
        okText="เพิ่ม"
        cancelText="ยกเลิก"
      >
        <AutoComplete
          style={{ width: '100%' }}
          placeholder="ค้นหาสินค้า (รหัสสินค้า หรือ ชื่อสินค้า)"
          value={productSearch}
          options={productOptions}
          onSearch={searchProducts}
          onSelect={handleProductSelect}
          onChange={setProductSearch}
        />

        {selectedProduct && (
          <Alert
            style={{ marginTop: 16 }}
            message={`เลือก: ${selectedProduct.barcode} - ${selectedProduct.name}`}
            description={`ราคาขาย: ${formatCurrency(selectedProduct.cost || 0)} | คงเหลือ: ${selectedProduct.stock || 0}`}
            type="info"
            showIcon
          />
        )}
      </Modal>
    </div>
  );
};

export default CreatePurchaseOrderPage;